import { SORT_BY_OPTIONS } from '../components/growth-stage-ventures/sort-by-menu/constants';
import { ISortByValue } from '../components/growth-stage-ventures/sort-by-menu/types';

/** Query param carrying the growth-stage sort, e.g. `?sort=most-funded`. */
export const SORT_QUERY_PARAM = 'sort';

/**
 * URL slug for every sort value. A total `Record` for the same reason as
 * `SORT_ENUM_BY_VALUE` in `to-offering-search-sort`: adding a menu option
 * without deciding its slug then fails to compile instead of silently
 * defaulting.
 *
 * The slugs are deliberately not the internal values — those are also emitted as
 * an analytics payload (see `OfferingsGrid`), so they can't be renamed to match
 * the URL without changing what analytics receives.
 *
 * `trending` maps to `null`: it is the default, so it is never written to the
 * URL, and an absent param means trending.
 */
const SLUG_BY_SORT_VALUE: Record<ISortByValue, string | null> = {
  trending: null,
  newest: 'recently-launched',
  'closing-soon': 'closing-soon',
  'most-funded': 'most-funded',
};

/**
 * Derived rather than written out a second time, so the two directions can't
 * drift. `trending` is absent by construction — its slug is `null`, which is why
 * `?sort=trending` reads as unrecognized and gets stripped.
 */
const SORT_VALUE_BY_SLUG = SORT_BY_OPTIONS.reduce<
  Partial<Record<string, ISortByValue>>
>((acc, { value }) => {
  const slug = SLUG_BY_SORT_VALUE[value];

  if (slug) {
    acc[slug] = value;
  }

  return acc;
}, {});

/** `null` for the default, which is never written to the URL. */
export const toSortSlug = (value: ISortByValue): string | null =>
  SLUG_BY_SORT_VALUE[value];

/**
 * `undefined` for anything that shouldn't stay in the URL: a missing param, an
 * unknown slug, or the default's own name. Callers fall back to
 * `DEFAULT_SORT_BY_VALUE` and strip the param.
 *
 * Next hands a repeated param (`?sort=a&sort=b`) over as an array; the first
 * entry wins, matching `useToastFromURLParams`.
 */
export const fromSortSlug = (
  raw: string | string[] | undefined
): ISortByValue | undefined => {
  const slug = typeof raw === 'string' ? raw : raw?.[0];

  return slug ? SORT_VALUE_BY_SLUG[slug] : undefined;
};
